import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  Alert,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Collapse,
  IconButton,
} from '@mui/material';
import { StatusChip } from '../../../common/components';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Assignment as ContractIcon,
  Info as InfoIcon,
  Key as KeyIcon,
} from '@mui/icons-material';

/**
 * Componente para mostrar los resultados del envío de emails con paginación
 */
const EmailResultsDialog = ({ open, onClose, results }) => {
  const [successPage, setSuccessPage] = useState(1);
  const [failedPage, setFailedPage] = useState(1);
  const [expandedContracts, setExpandedContracts] = useState(new Set());

  const ITEMS_PER_PAGE = 10;

  const groupEmailsByContract = useMemo(
    () => (emails) => {
      const grouped = {};
      const safeEmails = Array.isArray(emails) ? emails : [];
      safeEmails.forEach((email) => {
        const contractNumber = email.order_number || 'Sin contrato';
        if (!grouped[contractNumber]) {
          grouped[contractNumber] = [];
        }
        grouped[contractNumber].push(email);
      });
      return grouped;
    },
    []
  );

  const successfulGrouped = useMemo(() => {
    if (!results) return {};
    return groupEmailsByContract(results.successful_emails || []);
  }, [results, groupEmailsByContract]);

  const failedGrouped = useMemo(() => {
    if (!results) return {};
    return groupEmailsByContract(results.failed_emails || []);
  }, [results, groupEmailsByContract]);

  if (!results) return null;

  const { message, summary = {} } = results;

  const paginateData = (data, page) => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return Object.entries(data).slice(startIndex, endIndex);
  };

  const successfulPaginated = paginateData(successfulGrouped, successPage);
  const failedPaginated = paginateData(failedGrouped, failedPage);

  const successfulTotalPages = Math.ceil(Object.keys(successfulGrouped).length / ITEMS_PER_PAGE);
  const failedTotalPages = Math.ceil(Object.keys(failedGrouped).length / ITEMS_PER_PAGE);

  const getStatusColor = (status) => {
    if (summary.failed_count === 0) return 'success';
    if (summary.successful_count === 0) return 'error';
    return 'warning';
  };

  const getStatusIcon = () => {
    if (summary.failed_count === 0) return <SuccessIcon color="success" />;
    if (summary.successful_count === 0) return <ErrorIcon color="error" />;
    return <InfoIcon color="warning" />;
  };

  const toggleContractExpanded = (contractNumber) => {
    const newExpanded = new Set(expandedContracts);
    if (newExpanded.has(contractNumber)) {
      newExpanded.delete(contractNumber);
    } else {
      newExpanded.add(contractNumber);
    }
    setExpandedContracts(newExpanded);
  };

  const renderContractList = (contractsData, type = 'success') => {
    if (!contractsData || contractsData.length === 0) return null;

    return (
      <List dense>
        {contractsData.map(([contractNumber, emails]) => {
          const isExpanded = expandedContracts.has(contractNumber);
          const contractKey = `${type}-${contractNumber}`;

          return (
            <Box key={contractKey} sx={{ mb: 1 }}>
              <ListItem
                sx={{
                  pl: 0,
                  backgroundColor: type === 'success' ? '#f8fdf8' : '#fef8f8',
                  borderRadius: 1,
                  border: 1,
                  borderColor: type === 'success' ? '#e8f5e8' : '#fde8e8',
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => toggleContractExpanded(contractNumber)}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <ContractIcon color={type === 'success' ? 'success' : 'error'} />
                      <Box>
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          color={type === 'success' ? 'success.dark' : 'error.dark'}
                        >
                          Contrato: {contractNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {emails.length} email{emails.length > 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <StatusChip
                        label={type === 'success' ? 'Enviado' : 'Error'}
                        color={type === 'success' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </Box>
                    <IconButton size="small">
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  <Collapse in={isExpanded}>
                    <Box
                      sx={{
                        mt: 2,
                        pl: 4,
                        borderLeft: 2,
                        borderColor: type === 'success' ? 'success.light' : 'error.light',
                        ml: 1,
                      }}
                    >
                      {emails.map((email, emailIndex) => (
                        <Box
                          key={emailIndex}
                          sx={{
                            mb: 2,
                            p: 2,
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: 1,
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <PersonIcon fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight="medium">
                              {email.email || 'Email no especificado'}
                            </Typography>
                          </Box>

                          {email.uuid_for_file && (
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <KeyIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                ID: {email.uuid_for_file}
                              </Typography>
                            </Box>
                          )}

                          {email.status_code && (
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <InfoIcon fontSize="small" color="info" />
                              <Typography variant="caption" color="info.main">
                                Código: {email.status_code}
                              </Typography>
                            </Box>
                          )}

                          {email.error && (
                            <Box display="flex" alignItems="start" gap={1}>
                              <ErrorIcon fontSize="small" color="error" />
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ wordBreak: 'break-word' }}
                              >
                                Error: {email.error}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              </ListItem>
            </Box>
          );
        })}
      </List>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          {getStatusIcon()}
          <Typography variant="h6" component="span">
            Resultado del Envío de Emails
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box mb={3}>
          <Alert severity={getStatusColor()} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="medium">
              {message}
            </Typography>
          </Alert>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="text.primary">
                    {summary.total_emails || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Emails
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="success.main">
                    {summary.successful_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Exitosos
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="error.main">
                    {summary.failed_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fallidos
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <Box>
          {Object.keys(successfulGrouped).length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SuccessIcon color="success" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Emails Enviados Exitosamente ({Object.keys(successfulGrouped).length} contratos)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {renderContractList(successfulPaginated, 'success')}
                {successfulTotalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={successfulTotalPages}
                      page={successPage}
                      onChange={(event, value) => setSuccessPage(value)}
                      color="success"
                      size="small"
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {Object.keys(failedGrouped).length > 0 && (
            <Accordion defaultExpanded={Object.keys(successfulGrouped).length === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <ErrorIcon color="error" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Emails Fallidos ({Object.keys(failedGrouped).length} contratos)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {renderContractList(failedPaginated, 'error')}
                {failedTotalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={failedTotalPages}
                      page={failedPage}
                      onChange={(event, value) => setFailedPage(value)}
                      color="error"
                      size="small"
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}
        </Box>

        {Object.keys(successfulGrouped).length === 0 && Object.keys(failedGrouped).length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No hay detalles de emails disponibles
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailResultsDialog;
